/* ── PAG Account Configuration helpers ──────────────────────────────────
   Pure helper functions for group and account data merging and column/risk calculations.
   Supports both directory groups and individual/ungrouped accounts.
   ──────────────────────────────────────────────────────────────────────── */

import type { RiskLevel } from "../constants/pagConstants";
import type { UnifiedColumn, UnifiedGroup } from "../constants/pagAccountConfigConstants";
import type {
  PagAccountItem,
  PagGroupRiskItem,
  PagOnPremAccountItem,
  PagOnPremGroup,
  PagOnPremGroupList,
} from "../services/pagApi";

export type PagAccountCandidate = PagAccountItem | PagOnPremAccountItem;

/**
 * Merges raw on-prem groups, privileged items, non-privileged groups, and
 * accounts into unified models with baseline privilege status and risk classification.
 * Handles both the new 6-section structure (availableGroups, availableAccounts,
 * privilegedGroups, privilegedAccounts, nonPrivilegedGroups, nonPrivilegedAccounts)
 * and legacy payloads.
 */
interface RawConfigPayloadCandidates {
  rawAvailableGroups: PagOnPremGroup[];
  rawAvailableAccounts: PagAccountCandidate[];
  rawPrivilegedGroups: PagOnPremGroup[];
  rawPrivilegedAccounts: PagAccountCandidate[];
  rawNonPrivilegedGroups: PagOnPremGroup[];
  rawNonPrivilegedAccounts: PagAccountCandidate[];
}

interface ConfigLookupIndexes {
  riskMap: Map<number | string, PagGroupRiskItem>;
  privilegedGroupMap: Map<number | string, PagOnPremGroup>;
  nonPrivilegedGroupMap: Map<number | string, PagOnPremGroup>;
  privilegedAccountMap: Map<number, PagAccountCandidate>;
  nonPrivilegedAccountMap: Map<number, PagAccountCandidate>;
}

interface GroupMembershipIndex {
  groupedAccountIds: Set<number>;
  groupedUsernames: Set<string>;
  groupedUserDns: Set<string>;
  accountIdToGroupId: Map<number, number>;
  usernameToGroupId: Map<string, number>;
  knownGroupIds: Set<number>;
  knownGroupNames: Set<string>;
}

/** Extract candidate groups and accounts from legacy array or 6-section payload. */
function extractRawCandidates(
  onPremGroupsOrList: PagOnPremGroup[] | PagOnPremGroupList,
  privilegedGroups: PagOnPremGroup[] = [],
): RawConfigPayloadCandidates {
  if (Array.isArray(onPremGroupsOrList)) {
    return {
      rawAvailableGroups: onPremGroupsOrList,
      rawAvailableAccounts: [],
      rawPrivilegedGroups: privilegedGroups,
      rawPrivilegedAccounts: [],
      rawNonPrivilegedGroups: [],
      rawNonPrivilegedAccounts: [],
    };
  }

  const payload =
    onPremGroupsOrList && typeof onPremGroupsOrList === "object" ? onPremGroupsOrList : {};
  let rawNonPrivilegedGroups: PagOnPremGroup[] = [];
  let rawNonPrivilegedAccounts: PagAccountCandidate[] = [];

  if (payload.nonPrivilegedGroups) {
    rawNonPrivilegedGroups = payload.nonPrivilegedGroups;
  } else if (payload.nonPrivileged) {
    if (Array.isArray(payload.nonPrivileged)) {
      rawNonPrivilegedGroups = payload.nonPrivileged;
    } else {
      rawNonPrivilegedGroups = payload.nonPrivileged.groups ?? [];
      rawNonPrivilegedAccounts = payload.nonPrivileged.ungroupedAccounts ?? [];
    }
  }

  if (payload.nonPrivilegedAccounts) {
    rawNonPrivilegedAccounts = payload.nonPrivilegedAccounts;
  }

  return {
    rawAvailableGroups: payload.availableGroups ?? payload.groups ?? [],
    rawAvailableAccounts: payload.availableAccounts ?? [],
    rawPrivilegedGroups: payload.privilegedGroups ?? payload.privileged ?? [],
    rawPrivilegedAccounts: payload.privilegedAccounts ?? [],
    rawNonPrivilegedGroups,
    rawNonPrivilegedAccounts,
  };
}

/** Build fast lookup index maps for risk classification and privilege status. */
function buildConfigLookupIndexes(
  rawCandidates: RawConfigPayloadCandidates,
  riskItems: PagGroupRiskItem[],
): ConfigLookupIndexes {
  const riskMap = new Map<number | string, PagGroupRiskItem>();
  for (const r of riskItems) {
    riskMap.set(r.groupId, r);
    riskMap.set(r.groupName.toLowerCase(), r);
  }

  const privilegedGroupMap = new Map<number | string, PagOnPremGroup>();
  for (const g of rawCandidates.rawPrivilegedGroups) {
    privilegedGroupMap.set(g.groupId, g);
    privilegedGroupMap.set(g.groupName.toLowerCase(), g);
  }

  const nonPrivilegedGroupMap = new Map<number | string, PagOnPremGroup>();
  for (const g of rawCandidates.rawNonPrivilegedGroups) {
    nonPrivilegedGroupMap.set(g.groupId, g);
    nonPrivilegedGroupMap.set(g.groupName.toLowerCase(), g);
  }

  const privilegedAccountMap = new Map<number, PagAccountCandidate>();
  for (const acc of rawCandidates.rawPrivilegedAccounts) {
    if (acc.accountId) privilegedAccountMap.set(acc.accountId, acc);
  }

  const nonPrivilegedAccountMap = new Map<number, PagAccountCandidate>();
  for (const acc of rawCandidates.rawNonPrivilegedAccounts) {
    if (acc.accountId) nonPrivilegedAccountMap.set(acc.accountId, acc);
  }

  return {
    riskMap,
    privilegedGroupMap,
    nonPrivilegedGroupMap,
    privilegedAccountMap,
    nonPrivilegedAccountMap,
  };
}

/** Determine baseline column category for a group or standalone account. */
function computeGroupBaselineCategory(
  isPriv: boolean,
  isNonPriv: boolean,
): "IDENTIFIED" | "NON_PRIVILEGED" | "PRIVILEGED" {
  if (isPriv) return "PRIVILEGED";
  if (isNonPriv) return "NON_PRIVILEGED";
  return "IDENTIFIED";
}

/** Merge directory group into mergedMap with risk levels and account counts. */
function mergeSingleGroup(
  g: PagOnPremGroup,
  mergedMap: Map<string, UnifiedGroup>,
  indexes: ConfigLookupIndexes,
): void {
  const key = `g-${g.groupId}`;
  const existing = mergedMap.get(key);
  if (existing) {
    if (g.accounts?.length && !existing.accounts?.length) {
      existing.accounts = g.accounts as PagAccountItem[];
      existing.accountCount = g.accounts.length;
    }
    if (g.riskLevel && !existing.baselineRisk) {
      existing.baselineRisk = g.riskLevel as RiskLevel;
    }
    return;
  }

  const privMatch =
    indexes.privilegedGroupMap.get(g.groupId) ??
    indexes.privilegedGroupMap.get(g.groupName.toLowerCase());
  const isPriv = privMatch !== undefined || g.classification === "PRIVILEGED";

  const nonPrivMatch =
    indexes.nonPrivilegedGroupMap.get(g.groupId) ??
    indexes.nonPrivilegedGroupMap.get(g.groupName.toLowerCase());
  const isNonPriv =
    !isPriv && (nonPrivMatch !== undefined || g.classification === "NON_PRIVILEGED");

  const baseCategory = computeGroupBaselineCategory(isPriv, isNonPriv);

  const risk = indexes.riskMap.get(g.groupId) ?? indexes.riskMap.get(g.groupName.toLowerCase());
  const rLevel =
    (g.riskLevel as RiskLevel | null) ??
    (privMatch?.riskLevel as RiskLevel | null) ??
    (risk?.riskLevel as RiskLevel | null) ??
    null;

  const accounts = (g.accounts ?? privMatch?.accounts ?? []) as PagAccountItem[];
  const accountCount =
    accounts.length > 0 ? accounts.length : (risk?.accountCount ?? g.memberCount ?? 0);

  mergedMap.set(key, {
    id: key,
    itemType: "GROUP",
    groupId: g.groupId,
    sourceSystemId: g.sourceSystemId,
    sourceSystemName: risk?.sourceSystemName,
    groupName: g.groupName,
    description: g.description ?? risk?.description ?? null,
    accountCount,
    accounts,
    groupScope: g.groupScope,
    groupCategory: g.groupCategory,
    baselinePrivileged: isPriv,
    baselineRisk: isPriv ? rLevel : null,
    baselineCategory: baseCategory,
  });
}

/** Build group membership index to identify accounts that belong to known directory groups. */
function buildGroupMembershipIndex(allGroups: PagOnPremGroup[]): GroupMembershipIndex {
  const index: GroupMembershipIndex = {
    groupedAccountIds: new Set<number>(),
    groupedUsernames: new Set<string>(),
    groupedUserDns: new Set<string>(),
    accountIdToGroupId: new Map<number, number>(),
    usernameToGroupId: new Map<string, number>(),
    knownGroupIds: new Set<number>(),
    knownGroupNames: new Set<string>(),
  };

  for (const g of allGroups) {
    if (g.groupId) index.knownGroupIds.add(g.groupId);
    if (g.groupName) index.knownGroupNames.add(g.groupName.toLowerCase().trim());

    for (const acc of g.accounts ?? []) {
      if (acc.accountId) {
        index.groupedAccountIds.add(acc.accountId);
        if (g.groupId) index.accountIdToGroupId.set(acc.accountId, g.groupId);
      }
      const u = acc.username || acc.userName;
      if (u) {
        const uLower = String(u).toLowerCase().trim();
        index.groupedUsernames.add(uLower);
        if (g.groupId) index.usernameToGroupId.set(uLower, g.groupId);
      }
      if (acc.userDn) {
        index.groupedUserDns.add(String(acc.userDn).toLowerCase().trim());
      }
    }
  }

  return index;
}

/** Check if an account is a member of an existing directory group. */
function isAccountMemberOfGroup(
  acc: PagAccountCandidate,
  membership: GroupMembershipIndex,
): boolean {
  if (acc.accountId && membership.groupedAccountIds.has(acc.accountId)) {
    return true;
  }
  const rawUserName = acc.username || acc.userName;
  if (rawUserName && membership.groupedUsernames.has(String(rawUserName).toLowerCase().trim())) {
    return true;
  }
  if (acc.userDn && membership.groupedUserDns.has(String(acc.userDn).toLowerCase().trim())) {
    return true;
  }
  if (
    typeof acc.groupId === "number" &&
    acc.groupId > 0 &&
    membership.knownGroupIds.has(acc.groupId)
  ) {
    return true;
  }
  if (acc.groupName && membership.knownGroupNames.has(String(acc.groupName).toLowerCase().trim())) {
    return true;
  }
  return false;
}

/** Find the target parent group for a member account across registered groups. */
function findTargetGroupForAccount(
  acc: PagAccountCandidate,
  mergedMap: Map<string, UnifiedGroup>,
  membership: GroupMembershipIndex,
): UnifiedGroup | undefined {
  const rawUserName = acc.username || acc.userName;
  const foundGroupId =
    (typeof acc.groupId === "number" && acc.groupId > 0 ? acc.groupId : undefined) ??
    (acc.accountId ? membership.accountIdToGroupId.get(acc.accountId) : undefined) ??
    (rawUserName
      ? membership.usernameToGroupId.get(String(rawUserName).toLowerCase().trim())
      : undefined);

  if (foundGroupId && mergedMap.has(`g-${foundGroupId}`)) {
    return mergedMap.get(`g-${foundGroupId}`);
  }

  const rawGroupName = acc.groupName ? String(acc.groupName).toLowerCase().trim() : undefined;
  for (const item of mergedMap.values()) {
    if (item.itemType === "GROUP") {
      if (foundGroupId && item.groupId === foundGroupId) return item;
      if (rawGroupName && item.groupName.toLowerCase().trim() === rawGroupName) return item;
    }
  }

  return undefined;
}

/** Update or append member account to parent directory group. */
function updateGroupWithAccount(targetGroup: UnifiedGroup, acc: PagAccountCandidate): void {
  if (!targetGroup.accounts) targetGroup.accounts = [];
  const rawUserName = acc.username || acc.userName;

  const existingAcc = targetGroup.accounts.find(
    (a) =>
      a.accountId === acc.accountId ||
      (rawUserName && (a.username === rawUserName || a.userName === rawUserName)),
  );

  if (existingAcc) {
    if (acc.riskLevel && !existingAcc.riskLevel) {
      existingAcc.riskLevel = acc.riskLevel as RiskLevel;
    }
    if (acc.displayName && !existingAcc.displayName) {
      existingAcc.displayName = acc.displayName;
    }
    if (acc.email && !existingAcc.email) {
      existingAcc.email = acc.email;
    }
  } else {
    targetGroup.accounts.push({
      sourceSystemId: acc.sourceSystemId || targetGroup.sourceSystemId,
      accountId: acc.accountId!,
      groupId: targetGroup.groupId,
      groupName: targetGroup.groupName,
      userName: rawUserName || `account_${acc.accountId}`,
      displayName: acc.displayName || rawUserName || `account_${acc.accountId}`,
      email: acc.email ?? null,
      userDn: acc.userDn ?? null,
      riskLevel: (acc.riskLevel as RiskLevel) ?? targetGroup.baselineRisk ?? null,
    });
    targetGroup.accountCount = targetGroup.accounts.length;
  }
}

/** Register standalone (ungrouped) account as an independent Kanban card. */
function mergeStandaloneAccount(
  acc: PagAccountCandidate,
  mergedMap: Map<string, UnifiedGroup>,
  indexes: ConfigLookupIndexes,
): void {
  const key = `a-${acc.accountId}`;
  const existing = mergedMap.get(key);
  if (existing) {
    if (acc.riskLevel && !existing.baselineRisk) {
      existing.baselineRisk = acc.riskLevel as RiskLevel;
    }
    return;
  }

  const isPriv =
    indexes.privilegedAccountMap.has(acc.accountId!) || acc.classification === "PRIVILEGED";
  const isNonPriv =
    !isPriv &&
    (indexes.nonPrivilegedAccountMap.has(acc.accountId!) ||
      acc.classification === "NON_PRIVILEGED");

  const baseCategory = computeGroupBaselineCategory(isPriv, isNonPriv);

  const rawUserName = acc.username || acc.userName;
  const userName = rawUserName || `account_${acc.accountId}`;
  const displayName = acc.displayName || userName;

  mergedMap.set(key, {
    id: key,
    itemType: "ACCOUNT",
    groupId: acc.groupId ?? 0,
    accountId: acc.accountId,
    sourceSystemId: acc.sourceSystemId,
    groupName: displayName,
    userName: userName,
    displayName: acc.displayName ?? null,
    email: acc.email ?? null,
    description: acc.email ?? acc.userDn ?? null,
    accountCount: 1,
    groupScope: acc.membershipType || acc.groupScope || "DIRECT",
    groupCategory: "ACCOUNT",
    baselinePrivileged: isPriv,
    baselineRisk: isPriv ? ((acc.riskLevel as RiskLevel | null) ?? null) : null,
    baselineCategory: baseCategory,
  });
}

/**
 * Merges raw on-prem groups, privileged items, non-privileged groups, and
 * accounts into unified models with baseline privilege status and risk classification.
 * Handles both the new 6-section structure (availableGroups, availableAccounts,
 * privilegedGroups, privilegedAccounts, nonPrivilegedGroups, nonPrivilegedAccounts)
 * and legacy payloads.
 */
export const mergeUnifiedGroups = (
  onPremGroupsOrList: PagOnPremGroup[] | PagOnPremGroupList,
  privilegedGroups: PagOnPremGroup[] = [],
  riskItems: PagGroupRiskItem[] = [],
): UnifiedGroup[] => {
  const raw = extractRawCandidates(onPremGroupsOrList, privilegedGroups);
  const indexes = buildConfigLookupIndexes(raw, riskItems);
  const mergedMap = new Map<string, UnifiedGroup>();

  const allGroups = [
    ...raw.rawPrivilegedGroups,
    ...raw.rawNonPrivilegedGroups,
    ...raw.rawAvailableGroups,
  ];

  for (const g of allGroups) {
    mergeSingleGroup(g, mergedMap, indexes);
  }

  const membership = buildGroupMembershipIndex(allGroups);
  const allAccounts: PagAccountCandidate[] = [
    ...raw.rawPrivilegedAccounts,
    ...raw.rawNonPrivilegedAccounts,
    ...raw.rawAvailableAccounts,
  ];

  for (const acc of allAccounts) {
    if (!acc.accountId) continue;

    if (isAccountMemberOfGroup(acc, membership)) {
      const targetGroup = findTargetGroupForAccount(acc, mergedMap, membership);
      if (targetGroup) {
        updateGroupWithAccount(targetGroup, acc);
      }
      continue;
    }

    mergeStandaloneAccount(acc, mergedMap, indexes);
  }

  return Array.from(mergedMap.values());
};

/** Determines the original/baseline Kanban column for a group/account based on its API state. */
export const getBaselineColumn = (group: UnifiedGroup): UnifiedColumn => {
  if (group.baselineCategory === "NON_PRIVILEGED") return "NON_PRIVILEGED";
  if (group.baselineCategory === "IDENTIFIED") return "IDENTIFIED";
  if (!group.baselinePrivileged) return "IDENTIFIED";
  if (group.baselineRisk === "CRITICAL") return "CRITICAL";
  if (group.baselineRisk === "HIGH") return "HIGH";
  if (group.baselineRisk === "MEDIUM") return "MEDIUM";
  if (group.baselineRisk === "LOW") return "LOW";
  return "UNASSIGNED";
};

/** Calculates the current Kanban column factoring in user edits (draft state). */
export const getCurrentColumn = (
  group: UnifiedGroup,
  draft: Record<string, UnifiedColumn>,
): UnifiedColumn => {
  const col = draft[group.id] ?? draft[String(group.groupId)] ?? getBaselineColumn(group);
  if (col === "AVAILABLE") return "IDENTIFIED";
  return col;
};

/** Checks whether a group/account is considered privileged in the current (or draft) state. */
export const isGroupPrivileged = (
  group: UnifiedGroup,
  draft: Record<string, UnifiedColumn>,
): boolean => {
  const col = getCurrentColumn(group, draft);
  return (
    col === "CRITICAL" ||
    col === "HIGH" ||
    col === "MEDIUM" ||
    col === "LOW" ||
    col === "UNASSIGNED"
  );
};

/** Checks whether a group/account is considered non-privileged in the current (or draft) state. */
export const isGroupNonPrivileged = (
  group: UnifiedGroup,
  draft: Record<string, UnifiedColumn>,
): boolean => getCurrentColumn(group, draft) === "NON_PRIVILEGED";

/** Checks whether a group/account is in the unassigned/identified bucket. */
export const isGroupIdentified = (
  group: UnifiedGroup,
  draft: Record<string, UnifiedColumn>,
): boolean => getCurrentColumn(group, draft) === "IDENTIFIED";

/** Extracts the risk level for a group/account in the current (or draft) state. */
export const getGroupRiskLevel = (
  group: UnifiedGroup,
  draft: Record<string, UnifiedColumn>,
): RiskLevel | null => {
  const col = getCurrentColumn(group, draft);
  if (col === "CRITICAL" || col === "HIGH" || col === "MEDIUM" || col === "LOW") return col;
  return null;
};
